import React from 'react';
import WatercolorWash from '../WatercolorWash';
import { useReveal } from '../../hooks/useReveal';
import type { PlaceLocation } from '../../data/site';
import '../../styles/Place.css';

interface PlaceProps {
  locations?: PlaceLocation[];
  footnote?: string;
  chapter?: string;
}

const PlaceMap: React.FC = () => (
  <div className="place__map">
    <svg viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        <pattern id="psl-grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M20 0L0 0 0 20" fill="none" stroke="#c25a3a" strokeWidth=".4" opacity=".32" />
        </pattern>
      </defs>
      <rect width="400" height="300" fill="#0f2a4a" />
      <rect width="400" height="300" fill="url(#psl-grid)" />

      {/* sea / coastline */}
      <path
        d="M0 140 Q60 130 100 150 T220 160 T400 140 L400 300 L0 300 Z"
        fill="#1f3c5f"
      />
      <path
        d="M0 140 Q60 130 100 150 T220 160 T400 140"
        stroke="#c25a3a"
        strokeWidth="1.6"
        fill="none"
      />

      {/* sea dashes */}
      <g stroke="#ebdcc0" strokeWidth="1" opacity=".35">
        <path d="M10 50 L40 50" />
        <path d="M70 68 L120 68" />
        <path d="M160 55 L210 55" />
        <path d="M260 80 L320 80" />
        <path d="M340 60 L380 60" />
        <path d="M30 100 L80 100" />
        <path d="M140 110 L200 110" />
        <path d="M280 120 L340 120" />
      </g>

      {/* roads */}
      <g stroke="#ebdcc0" strokeWidth="1" strokeDasharray="4 3" fill="none" opacity=".7">
        <path d="M200 300 L200 200 Q200 180 210 170" />
        <path d="M60 260 L340 250" />
      </g>

      {/* compass */}
      <g transform="translate(352 252)" fill="#ebdcc0">
        <text x="-3" y="-18" fontFamily="Italiana" fontSize="12">N</text>
        <path d="M0 -14 L4 0 L0 -4 L-4 0 Z" fill="#c25a3a" />
      </g>
    </svg>

    <div className="place__pin place__pin--canil">
      Silvalde · Canil
      <span className="place__pin-dot" aria-hidden="true" />
    </div>
    <div className="place__pin place__pin--gatil">
      Espinho · Gatil
      <span className="place__pin-dot" aria-hidden="true" />
    </div>
    <div className="place__aqui">aqui!</div>
  </div>
);

const DEFAULT_LOCATIONS: PlaceLocation[] = [
  {
    label: 'Canil',
    address: ['Rua do Calvário, 411', '4500-619 Silvalde, Espinho', 'Portugal'],
    hint:
      'A entrada é pelo portão entre os ecopontos e o 411 — segue o latido. Voluntariado e visitas ao fim de semana de manhã; outras alturas por marcação.',
    marginNote: '— entre os ecopontos e o 411.',
  },
  {
    label: 'Gatil',
    address: ['Rua do Golfe, 1877', '4500-389 Espinho', 'Portugal'],
    hint:
      'Os gatos vivem no nosso espaço em Espinho. Voluntariado ao fim de semana de manhã; visitas por marcação.',
    marginNote: '— a poucos minutos do canil.',
  },
];

const Place: React.FC<PlaceProps> = ({
  locations = DEFAULT_LOCATIONS,
  footnote = 'aprox. 3 km da praia · 25 min do Porto',
  chapter = 'Cap. IV · Onde estamos',
}) => {
  const { ref, revealed } = useReveal<HTMLElement>();
  return (
  <section
    ref={ref}
    className={`place psl-reveal${revealed ? ' is-in-view' : ''}`}
    id="place"
  >
    <WatercolorWash variant="c" color="#cf6f5a" opacity={0.22} style={{ top: '-60px', left: '-60px' }} />

    <div>
      <div className="place__chapter">{chapter}</div>
      <h2 className="place__head">
        Silvalde &amp; Espinho,<br />
        <em>mesmo ao pé do mar</em>.
      </h2>

      <div className="place__locations">
        {locations.map((loc) => (
          <div key={loc.label} className="place__location">
            <div className="place__location-label">— {loc.label}</div>
            <p className="place__addr">
              {loc.address.map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < loc.address.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
            {loc.hint && <p className="place__hint">{loc.hint}</p>}
            {loc.marginNote && (
              <span className="place__margin-note">{loc.marginNote}</span>
            )}
          </div>
        ))}
      </div>
    </div>

    <div className="place__map-wrap">
      <PlaceMap />
      <div className="place__footnote">{footnote}</div>
    </div>
  </section>
  );
};

export default Place;
